import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Group,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import {
    IconCalendar,
    IconCalendarMonth,
    IconCalendarStats,
    IconCalendarWeek,
    IconInfoCircle,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { getAgentSubmissions, listAgentSubmissionJobs, listReportingAgents } from '../../api/resources';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { DEFAULT_PAGE_SIZE } from '../../hooks/useListQuery';
import type { AgentSubmissionJobRow, ReportingAgent, SubmissionPeriod } from '../../types/api';

function agentName(agent: ReportingAgent): string {
    const name = `${agent.firstName} ${agent.lastName}`.trim();
    return name || agent.email || agent.id;
}

function formatDateTime(value: string | null): string {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function CounterCard({
    label,
    value,
    icon,
    color,
    tooltip,
    selected,
    onClick,
}: {
    label: string;
    value: number;
    icon: typeof IconCalendar;
    color: string;
    tooltip: string;
    selected?: boolean;
    onClick?: () => void;
}) {
    const Icon = icon;
    return (
        <UnstyledButton onClick={onClick} w="100%">
            <Paper
                p="lg"
                radius="lg"
                withBorder
                shadow={selected ? 'sm' : 'xs'}
                style={{
                    borderColor: selected ? `var(--mantine-color-${color}-filled)` : undefined,
                    backgroundColor: selected ? `var(--mantine-color-${color}-light)` : undefined,
                    cursor: onClick ? 'pointer' : undefined,
                }}
            >
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Stack gap={4}>
                        <Group gap={6} wrap="nowrap">
                            <Text size="sm" c="dimmed">
                                {label}
                            </Text>
                            <Tooltip
                                label={tooltip}
                                multiline
                                w={260}
                                withArrow
                                events={{ hover: true, focus: true, touch: true }}
                            >
                                <ThemeIcon variant="subtle" color="gray" size="xs" radius="xl">
                                    <IconInfoCircle size={14} stroke={1.5} />
                                </ThemeIcon>
                            </Tooltip>
                        </Group>
                        <Title order={3}>{value}</Title>
                    </Stack>
                    <ThemeIcon variant="light" color={color} size="lg" radius="md">
                        <Icon size={18} stroke={1.5} />
                    </ThemeIcon>
                </Group>
            </Paper>
        </UnstyledButton>
    );
}

const PERIOD_CARDS: Array<{
    period: SubmissionPeriod;
    icon: typeof IconCalendar;
    color: string;
    countKey: 'daily' | 'weekly' | 'monthly' | 'yearly';
}> = [
    { period: 'daily', icon: IconCalendar, color: 'violet', countKey: 'daily' },
    { period: 'weekly', icon: IconCalendarWeek, color: 'blue', countKey: 'weekly' },
    { period: 'monthly', icon: IconCalendarMonth, color: 'teal', countKey: 'monthly' },
    { period: 'yearly', icon: IconCalendarStats, color: 'grape', countKey: 'yearly' },
];

export function AgentDashboardPage() {
    const { t } = useTranslation();
    const [agentId, setAgentId] = useState<string | null>('');
    const [selectedPeriod, setSelectedPeriod] = useState<SubmissionPeriod | null>(null);
    const [jobsPage, setJobsPage] = useState(1);

    const agentsQ = useQuery({
        queryKey: ['reporting-agents'],
        queryFn: () => listReportingAgents(),
    });

    const agents = useMemo(() => agentsQ.data?.data ?? [], [agentsQ.data]);

    const selectedAgentId = agentId ?? '';
    const isAllAgents = selectedAgentId === '';

    const submissionsQ = useQuery({
        queryKey: ['agent-submissions', selectedAgentId],
        queryFn: () => getAgentSubmissions(isAllAgents ? {} : { agentId: selectedAgentId }),
        enabled: true,
    });

    const jobsParams = useMemo(
        () => ({
            period: selectedPeriod!,
            ...(isAllAgents ? {} : { agentId: selectedAgentId }),
            page: jobsPage,
            limit: DEFAULT_PAGE_SIZE,
        }),
        [selectedPeriod, selectedAgentId, isAllAgents, jobsPage],
    );

    const jobsQ = useQuery({
        queryKey: ['agent-submission-jobs', jobsParams],
        queryFn: () => listAgentSubmissionJobs(jobsParams),
        enabled: Boolean(selectedPeriod),
    });

    const submissions = submissionsQ.data;
    const selectedAgent = isAllAgents ? null : agents.find((a) => a.id === selectedAgentId);

    const handlePeriodSelect = (period: SubmissionPeriod) => {
        setSelectedPeriod(period);
        setJobsPage(1);
    };

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.agentDashboard.title')}
                description={
                    selectedAgent
                        ? t('features.agentDashboard.description', { name: agentName(selectedAgent) })
                        : t('features.agentDashboard.descriptionAll')
                }
                actions={
                    <Stack gap={4}>
                        <Text size="xs" fw={600} c="dimmed">
                            {t('features.agentDashboard.viewingAgent')}
                        </Text>
                        <Select
                            placeholder={t('features.agentDashboard.selectAgent')}
                            searchable
                            nothingFoundMessage={t('features.agentDashboard.noAgents')}
                            data={[
                                { value: '', label: t('features.agentDashboard.allAgents') },
                                ...agents.map((a) => ({ value: a.id, label: agentName(a) })),
                            ]}
                            value={selectedAgentId}
                            onChange={(value) => setAgentId(value ?? '')}
                            w={260}
                        />
                    </Stack>
                }
            />

            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
                {PERIOD_CARDS.map(({ period, icon, color, countKey }) => (
                    <CounterCard
                        key={period}
                        label={t(`features.agentDashboard.counters.${countKey}`)}
                        value={submissions?.[countKey] ?? 0}
                        icon={icon}
                        color={color}
                        tooltip={t(`features.agentDashboard.tooltips.${countKey}`)}
                        selected={selectedPeriod === period}
                        onClick={() => handlePeriodSelect(period)}
                    />
                ))}
            </SimpleGrid>

            {selectedPeriod ? (
                <Stack gap="sm">
                    <Title order={4}>{t(`features.agentDashboard.jobsTitle.${selectedPeriod}`)}</Title>
                    <PaginatedTable
                        rowKey={(r: AgentSubmissionJobRow) => r.reportId}
                        columns={[
                            {
                                key: 'customer',
                                header: t('features.report.columns.customer'),
                                render: (r) => (
                                    <EntityRef
                                        title={r.customerName ?? t('features.report.unknownCustomer')}
                                        id={r.caseId}
                                        href={`/cases/${r.caseId}`}
                                    />
                                ),
                            },
                            {
                                key: 'status',
                                header: t('features.report.columns.status'),
                                render: (r) =>
                                    t(`visitStatuses.${r.status}`, { defaultValue: r.status }),
                            },
                            {
                                key: 'submittedAt',
                                header: t('features.agentDashboard.columns.submittedAt'),
                                render: (r) => formatDateTime(r.submittedAt),
                            },
                        ]}
                        rows={jobsQ.data?.data ?? []}
                        total={jobsQ.data?.total ?? 0}
                        page={jobsPage}
                        pageSize={DEFAULT_PAGE_SIZE}
                        onPageChange={setJobsPage}
                        isLoading={jobsQ.isLoading}
                        emptyMessage={t('features.agentDashboard.jobsEmpty')}
                    />
                </Stack>
            ) : null}
        </Stack>
    );
}
